import { useFolderStore } from '../stores/folderStore';
import { useTaskStore } from '../stores/taskStore';
import { normalizeCategory } from '../components/AIChat/SuggestionPreview';
import type { FolderDisplayMode } from '../types';

/**
 * ⭐⭐ APPLY AN AI "task folder" SUGGESTION (owner, 2026-09-17).
 *
 * The assistant could offer loose tasks or a multi-phase project, and nothing in
 * between — so "make me a grocery list folder" came back as ten flat tasks, or
 * as a Project. A folder is the app's own answer to that request: a named list
 * of one-off tasks the user ticks off and REPLAYS next week.
 *
 * ⚠️ The folder is created FIRST and its id put on every task: a task written
 * without `folderId` lands in Unassigned, which is exactly the outcome the
 * owner reported.
 */
export interface TaskFolderTask {
  title: string;
  description?: string;
  priority?: string;
  duration?: number;
  /** Checklist steps — each becomes a real CHILD task (owner, 2026-09-17). */
  subtasks?: unknown;
}

export interface TaskFolderSuggestion {
  name?: string;
  title?: string;
  description?: string;
  category?: string;
  mode?: string;
  tasks?: TaskFolderTask[];
}

const MODES: FolderDisplayMode[] = ['sequential', 'random', 'list'];
const normalizeMode = (m?: string): FolderDisplayMode =>
  (MODES as string[]).includes(String(m ?? '').toLowerCase().trim())
    ? (String(m).toLowerCase().trim() as FolderDisplayMode)
    : 'sequential';

/**
 * ⭐⭐ A SUBTASK IS A REAL CHILD TASK (`parentTaskId`), in the parent's domain and
 * folder — the same thing the task inspector's "Add a subtask" creates, not the
 * legacy `subTasks[]` array. The model may write them as strings or as
 * `{ title }`; blanks and repeats are dropped.
 */
export function subtaskTitles(t: { subtasks?: unknown } | null | undefined): string[] {
  const raw = Array.isArray(t?.subtasks) ? t!.subtasks : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of raw) {
    const title = (typeof s === 'string' ? s : (s as { title?: unknown })?.title);
    if (typeof title !== 'string') continue;
    const clean = title.trim();
    if (!clean || seen.has(clean.toLowerCase())) continue;
    seen.add(clean.toLowerCase());
    out.push(clean);
  }
  return out;
}

const PRIORITIES = ['high', 'medium', 'low'];
const normalizePriority = (p?: string): string =>
  PRIORITIES.includes(String(p ?? '').toLowerCase().trim()) ? String(p).toLowerCase().trim() : 'medium';

/** The folder's tasks, cleaned: titled, de-duplicated by title, in the model's order. */
export function folderTasks(s: TaskFolderSuggestion): TaskFolderTask[] {
  const seen = new Set<string>();
  return (Array.isArray(s?.tasks) ? s.tasks : [])
    .filter((t): t is TaskFolderTask => !!t && typeof t.title === 'string' && t.title.trim().length > 0)
    .map((t) => ({ ...t, title: t.title.trim() }))
    .filter((t) => {
      const key = t.title.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function folderName(s: TaskFolderSuggestion): string {
  return (s?.name ?? s?.title ?? '').trim();
}

/**
 * Create the folder and its tasks. Returns the ids, or null when the suggestion
 * carries no name or no usable task (nothing is written in that case).
 */
export async function applyTaskFolder(s: TaskFolderSuggestion): Promise<{ folderId: string; taskIds: string[] } | null> {
  const name = folderName(s);
  const tasks = folderTasks(s);
  if (!name || tasks.length === 0) return null;

  const category = normalizeCategory(s?.category);
  const mode = normalizeMode(s?.mode);
  const folderId = await useFolderStore.getState().addFolder({
    name,
    description: s?.description?.trim() || undefined,
    category,
  });
  // ⚠️ `addFolder` cannot take the mode (the store fills its own defaults), so
  // a chosen one is written after — with the replay mode kept coherent.
  if (mode !== 'sequential') {
    await useFolderStore.getState().updateFolder(folderId, {
      displayMode: mode,
      defaultReplayMode: mode === 'random' ? 'random' : 'sequential',
    });
  }

  const taskIds: string[] = [];
  tasks.forEach((t, i) => {
    taskIds.push(createTaskWithSubtasks(t, { category, folderId, order: i }));
  });
  return { folderId, taskIds };
}

/**
 * Create one task and its subtasks as child tasks. Returns the parent's id.
 * Shared by the folder applier and the loose-tasks applier, so a task carries
 * its checklist wherever the model puts it.
 */
export function createTaskWithSubtasks(
  t: TaskFolderTask,
  opts: { category: string; folderId?: string; order?: number },
): string {
  const addTask = useTaskStore.getState().addTask as (task: unknown) => unknown;
  const now = new Date().toISOString();
  const id = crypto.randomUUID();
  addTask({
    id,
    title: t.title,
    description: t.description || '',
    category: opts.category,
    priority: normalizePriority(t.priority),
    duration: typeof t.duration === 'number' && t.duration > 0 ? t.duration : 15,
    completed: false,
    status: 'categorized',
    ...(opts.folderId ? { folderId: opts.folderId } : {}),
    ...(opts.order !== undefined ? { order: opts.order } : {}),
    createdAt: now,
    updatedAt: now,
  });
  subtaskTitles(t).forEach((title, i) => {
    addTask({
      id: crypto.randomUUID(),
      title,
      completed: false,
      category: opts.category,
      status: 'categorized',
      ...(opts.folderId ? { folderId: opts.folderId } : {}),
      parentTaskId: id,
      order: i,
      createdAt: now,
      updatedAt: now,
    });
  });
  return id;
}
